/**
 * Amazon Ads — Sponsored Brands v3 Column Map (Story 19.15, Task 3)
 *
 * Single source of truth for the 24 priority SB metrics + 4 SB-only dimensions
 * defined in Story 19.15 AC #4. Every entry uses the EXACT API field name from
 * `_bmad/bmm/data/integration-metrics/amazon-ads.md § Sponsored Brands`.
 *
 * **Defensive contract:** if Amazon ever renames a column, the
 * `amazon_ads_column_drift_detected` warning fires at runtime AND the v2→v3
 * golden snapshot test (sb-column-map.test.ts) fails in CI per AC #4.
 *
 * **Why this file is kept tiny:** the value-add of having a single file
 * enumerating exactly which SB columns ProvenLeap exposes makes "what does this
 * story ship?" instantly answerable for an acquirer or a future engineer
 * (build-to-sell directive § Documentation).
 *
 * Out of scope (ships in 19.17 with widget presets): `sbAds`, `sbTargeting`,
 * `sbSearchTerm`, `sbRufusPrompts`, `sbPlacement`, `sbGrossAndInvalids`. SB
 * Search Term is privacy-deferred (Round 1 Privacy Officer).
 */

import type { DimensionDefinition, MetricDefinition } from "../adapter";
// Story 35.7 (Code Review Round 2) — `numericOrNull` is the single shared
// Round 3 Vuln 3 numeric guard. Previously SB had a private duplicate; dedup
// keeps SB/SP/SD/DSP numeric coercion behaviorally identical (including the
// Round 2 empty-string → null fix).
import { numericOrNull } from "./reports";

// ============================================================================
// Report-type union (3 SB report-type IDs supported in 19.15)
// ============================================================================

export const SB_REPORT_TYPE_IDS = [
  "sbCampaigns",
  "sbAdGroup",
  "sbPurchasedProduct",
] as const;

export type SbReportTypeId = (typeof SB_REPORT_TYPE_IDS)[number];

// ============================================================================
// Metric column map (AC #4 — 24 priority metrics)
// ============================================================================

export type SbMetricDataType =
  | "Integer"
  | "Currency"
  | "Percentage"
  | "Ratio"
  | "Decimal"
  | "String";

export interface SbMetricColumnEntry {
  /** Canonical key (lowerCamelCase, matches Amazon's API field names). */
  readonly key: string;
  /** Human-readable label surfaced in widget configs. */
  readonly label: string;
  /** Which SB report type carries this column. */
  readonly reportTypeId: SbReportTypeId;
  /** Exact API field name on the wire. */
  readonly columnName: string;
  /** Data type for downstream formatting. */
  readonly dataType: SbMetricDataType;
  /**
   * Story 19.15 AC #4 — attribution window in days. SB is uniformly 14d (Amazon
   * does NOT expose other attribution windows for SB). Round 2 Pre-mortem D —
   * prevents accidental cross-window aggregation in the 19.6 PPC Unified Widget.
   */
  readonly attributionWindowDays: 14;
  /** Compatible widget IDs (from integration-mapping-directive § 3). */
  readonly compatibleWidgets: readonly string[];
  /** Story 31-31 — aggregation semantics, carried into the MetricDefinition projection. */
  readonly aggregationKind?: MetricDefinition["aggregationKind"];
  readonly ratioComponents?: MetricDefinition["ratioComponents"];
}

/**
 * The 24 priority SB metrics ("branded reporting golden path") chosen via
 * Round 4 stakeholder QA architect's prioritization. Order matches AC #4 for
 * easy review.
 *
 * Categories:
 *   - SB Core Delivery (4): impressions, clicks, cost, viewableImpressions
 *   - SB Purchases — 14d (3): sales14d, orders14d, unitsSold14d
 *   - SB New To Brand — 14d (6): NTB sales/purchases/units + 3× percentages
 *   - SB Branded Search + DPV (3): brandedSearches, detailPageViewsClicks, attributionType
 *   - SB Video Quartile (6): video complete/quartiles/unmutes/5s
 *   - SB Performance Ratios (2 — derived): viewableRate, clickThroughRate
 */
export const SB_V3_COLUMN_MAP: Record<string, SbMetricColumnEntry> = {
  // --- Core Delivery (4) ---
  impressions: {
    key: "impressions",
    label: "Impressions",
    reportTypeId: "sbCampaigns",
    columnName: "impressions",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  clicks: {
    key: "clicks",
    label: "Clicks",
    reportTypeId: "sbCampaigns",
    columnName: "clicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  cost: {
    key: "cost",
    label: "Cost (Spend)",
    reportTypeId: "sbCampaigns",
    columnName: "cost",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  viewableImpressions: {
    key: "viewableImpressions",
    label: "Viewable Impressions",
    reportTypeId: "sbCampaigns",
    columnName: "viewableImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },

  // --- Purchases (14d only) (3) ---
  sales14d: {
    key: "sales14d",
    label: "Sales (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "sales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  orders14d: {
    key: "orders14d",
    label: "Orders (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "orders14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  unitsSold14d: {
    key: "unitsSold14d",
    label: "Units Sold (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "unitsSold14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },

  // --- New To Brand (14d) (6) ---
  newToBrandSales14d: {
    key: "newToBrandSales14d",
    label: "New-To-Brand Sales (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "newToBrandSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  newToBrandPurchases14d: {
    key: "newToBrandPurchases14d",
    label: "New-To-Brand Purchases (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "newToBrandPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  newToBrandUnitsSold14d: {
    key: "newToBrandUnitsSold14d",
    label: "New-To-Brand Units Sold (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "newToBrandUnitsSold14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  newToBrandSalesPercentage14d: {
    key: "newToBrandSalesPercentage14d",
    label: "New-To-Brand Sales % (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "newToBrandSalesPercentage14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newToBrandSales14d", denominator: "sales14d" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  newToBrandPurchasesPercentage14d: {
    key: "newToBrandPurchasesPercentage14d",
    label: "New-To-Brand Purchases % (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "newToBrandPurchasesPercentage14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newToBrandPurchases14d", denominator: "purchases14d" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  newToBrandUnitsSoldPercentage14d: {
    key: "newToBrandUnitsSoldPercentage14d",
    label: "New-To-Brand Units Sold % (14d)",
    reportTypeId: "sbPurchasedProduct",
    columnName: "newToBrandUnitsSoldPercentage14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newToBrandUnitsSold14d", denominator: "unitsSold14d" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },

  // --- Branded Search + DPV (3) ---
  // Round 3 Vuln 4 — keys are typed per ad-product so SB `brandedSearches`
  // (no 14d suffix) cannot conflate with SD's `brandedSearches` or be silently
  // mapped to SP fields. Per integration-mapping directive § 4.
  brandedSearches: {
    key: "brandedSearches",
    label: "Branded Searches",
    reportTypeId: "sbCampaigns",
    columnName: "brandedSearches",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  detailPageViewsClicks: {
    key: "detailPageViewsClicks",
    label: "Detail Page Views (Clicks)",
    reportTypeId: "sbCampaigns",
    columnName: "detailPageViewsClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  attributionType: {
    key: "attributionType",
    label: "Attribution Type",
    reportTypeId: "sbPurchasedProduct",
    columnName: "attributionType",
    dataType: "String",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },

  // --- Video Quartile Views (6) ---
  videoCompleteViews: {
    key: "videoCompleteViews",
    label: "Video Complete Views",
    reportTypeId: "sbCampaigns",
    columnName: "videoCompleteViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  videoFirstQuartileViews: {
    key: "videoFirstQuartileViews",
    label: "Video First-Quartile Views",
    reportTypeId: "sbCampaigns",
    columnName: "videoFirstQuartileViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  videoMidpointViews: {
    key: "videoMidpointViews",
    label: "Video Midpoint Views",
    reportTypeId: "sbCampaigns",
    columnName: "videoMidpointViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  videoThirdQuartileViews: {
    key: "videoThirdQuartileViews",
    label: "Video Third-Quartile Views",
    reportTypeId: "sbCampaigns",
    columnName: "videoThirdQuartileViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  videoUnmutes: {
    key: "videoUnmutes",
    label: "Video Unmutes",
    reportTypeId: "sbCampaigns",
    columnName: "videoUnmutes",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  video5SecondViews: {
    key: "video5SecondViews",
    label: "Video 5-Second Views",
    reportTypeId: "sbCampaigns",
    columnName: "video5SecondViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },

  // --- Performance Ratios (2 — derived) ---
  viewableRate: {
    key: "viewableRate",
    label: "Viewable Rate",
    reportTypeId: "sbCampaigns",
    columnName: "viewableRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "viewableImpressions", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  clickThroughRate: {
    key: "clickThroughRate",
    label: "Click-Through Rate",
    reportTypeId: "sbCampaigns",
    columnName: "clickThroughRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },

  // Story 19.16.1 AC #11 — SB AllViews baseline columns (14d only).
  purchasesAllViews14d: {
    key: "purchasesAllViews14d",
    label: "Purchases — All Views (14d)",
    reportTypeId: "sbCampaigns",
    columnName: "purchasesAllViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  salesAllViews14d: {
    key: "salesAllViews14d",
    label: "Sales — All Views (14d)",
    reportTypeId: "sbCampaigns",
    columnName: "salesAllViews14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
};

// Compile-time + runtime invariant — exactly 24 priority SB metrics.
// `as const` would fight the dynamic Record; the test asserts the count.

// ============================================================================
// Dimension column map (AC #12)
// ============================================================================

export interface SbDimensionColumnEntry {
  readonly key: string;
  readonly label: string;
  readonly reportTypeId: SbReportTypeId | "any";
  readonly columnName: string;
  readonly groupByValue: string;
}

/**
 * 4 NEW SB-specific dimensions (campaignId/Name/Status carry forward from SP
 * map and are reused, NOT duplicated; that's the "ANY" bucket in
 * pickSbReportType — Round 4 Gap 4 + AC #12).
 */
export const SB_V3_DIMENSION_MAP: Record<string, SbDimensionColumnEntry> = {
  adGroupId: {
    key: "adGroupId",
    label: "Ad Group ID (SB)",
    reportTypeId: "sbAdGroup",
    columnName: "adGroupId",
    groupByValue: "adGroup",
  },
  adGroupName: {
    key: "adGroupName",
    label: "Ad Group (SB)",
    reportTypeId: "sbAdGroup",
    columnName: "adGroupName",
    groupByValue: "adGroup",
  },
  purchasedAsin: {
    key: "purchasedAsin",
    label: "Purchased ASIN",
    reportTypeId: "sbPurchasedProduct",
    columnName: "purchasedAsin",
    groupByValue: "purchasedAsin",
  },
  attributionType: {
    key: "attributionType",
    label: "Attribution Type",
    reportTypeId: "sbPurchasedProduct",
    columnName: "attributionType",
    groupByValue: "purchasedAsin",
  },
  // Story 19.16.1 AC #3 — Sponsored Brands Rufus prompts dimension extension
  // (GA March 25, 2026). 256-char truncation invariant enforced via
  // `truncateSbPromptText` before persistence.
  promptText: {
    key: "promptText",
    label: "Prompt Text (SB)",
    reportTypeId: "sbCampaigns",
    columnName: "promptText",
    groupByValue: "promptText",
  },
  // Story 35.7 AC #7 (Code Review Round 3) — Portfolio + Campaign Budget
  // cluster shared across SP/SB/SD. SB's picker (`pickSbReportType`) accepts
  // these as `ANY_DIMS`; the dim-map registration here lets `buildSbColumnList`
  // resolve them to wire-format column names rather than silently dropping.
  // Verified May 2026 via Amazon Ads v3 SB OpenAPI — these columns surface on
  // all SB campaign-level reports.
  portfolioId: {
    key: "portfolioId",
    label: "Portfolio ID",
    reportTypeId: "any",
    columnName: "portfolioId",
    groupByValue: "campaign",
  },
  campaignBudgetAmount: {
    key: "campaignBudgetAmount",
    label: "Campaign Budget Amount",
    reportTypeId: "any",
    columnName: "campaignBudgetAmount",
    groupByValue: "campaign",
  },
  campaignBudgetCurrencyCode: {
    key: "campaignBudgetCurrencyCode",
    label: "Campaign Budget Currency Code",
    reportTypeId: "any",
    columnName: "campaignBudgetCurrencyCode",
    groupByValue: "campaign",
  },
  campaignRuleBasedBudgetAmount: {
    key: "campaignRuleBasedBudgetAmount",
    label: "Campaign Rule-Based Budget Amount",
    reportTypeId: "any",
    columnName: "campaignRuleBasedBudgetAmount",
    groupByValue: "campaign",
  },
  campaignApplicableBudgetRuleId: {
    key: "campaignApplicableBudgetRuleId",
    label: "Campaign Applicable Budget Rule ID",
    reportTypeId: "any",
    columnName: "campaignApplicableBudgetRuleId",
    groupByValue: "campaign",
  },
  campaignApplicableBudgetRuleName: {
    key: "campaignApplicableBudgetRuleName",
    label: "Campaign Applicable Budget Rule Name",
    reportTypeId: "any",
    columnName: "campaignApplicableBudgetRuleName",
    groupByValue: "campaign",
  },
  biddingStrategy: {
    key: "biddingStrategy",
    label: "Campaign Bidding Strategy",
    reportTypeId: "any",
    columnName: "biddingStrategy",
    groupByValue: "campaign",
  },
  campaignBudgetType: {
    key: "campaignBudgetType",
    label: "Campaign Budget Type",
    reportTypeId: "any",
    columnName: "campaignBudgetType",
    groupByValue: "campaign",
  },
};

/** Story 19.16.1 AC #3 — 256-char truncation invariant for SB prompt text. */
export const AMAZON_ADS_SB_PROMPT_TEXT_MAX_LENGTH = 256;

export function truncateSbPromptText(value: string): string {
  if (typeof value !== "string") return "";
  if (value.length <= AMAZON_ADS_SB_PROMPT_TEXT_MAX_LENGTH) return value;
  return value.slice(0, AMAZON_ADS_SB_PROMPT_TEXT_MAX_LENGTH);
}

// ============================================================================
// Public arrays (consumed by getAvailableMetrics / getAvailableDimensions)
// ============================================================================

/** AC #4 — exactly 24 priority SB metrics. */
export const AMAZON_ADS_SB_METRICS: MetricDefinition[] = Object.values(
  SB_V3_COLUMN_MAP
).map((m) => ({
  key: m.key,
  label: m.label,
  type:
    m.dataType === "Currency"
      ? "currency"
      : m.dataType === "Percentage"
        ? "percentage"
        : "number",
  category: "Amazon Ads SB",
  dataType:
    m.dataType === "String" ? "Enum" : m.dataType === "Decimal" ? "Decimal" : m.dataType,
  // Story 31-31 — carry aggregation metadata through the projection.
  ...(m.aggregationKind !== undefined ? { aggregationKind: m.aggregationKind } : {}),
  ...(m.ratioComponents !== undefined ? { ratioComponents: m.ratioComponents } : {}),
  compatibleWidgets: [...m.compatibleWidgets],
  apiField: m.columnName,
}));

/** AC #12 — 4 NEW SB-only dimensions (campaign* are shared with SP). */
export const AMAZON_ADS_SB_DIMENSIONS: DimensionDefinition[] = Object.values(
  SB_V3_DIMENSION_MAP
).map((d) => ({
  key: d.key,
  label: d.label,
  type:
    d.key === "purchasedAsin"
      ? "Product"
      : d.key === "attributionType"
        ? "Classification"
        : "Structure",
}));

// ============================================================================
// Pure helper: pick report type from dimensions (AC #1)
// ============================================================================

export type PickSbReportTypeResult =
  | {
      readonly ok: true;
      readonly reportTypeId: SbReportTypeId;
      readonly warnings: string[];
    }
  | {
      readonly ok: false;
      readonly error:
        | "unsupported_dimension_combination"
        | "ad_product_dimension_mismatch";
      readonly message: string;
    };

/**
 * Deterministic pure function that maps a requested dimension set to the
 * correct SB report type. Order:
 *
 *   1. `purchasedAsin`/`attributionType` ∈ dim → sbPurchasedProduct
 *   2. `adGroupId`/`adGroupName` ∈ dim → sbAdGroup
 *   3. dim ⊆ {campaign*} → sbCampaigns
 *   4. SD-only dim (`promotedAsin`, `promotedSku`, `targetingExpression`) →
 *      ad_product_dimension_mismatch (Round 3 Vuln 4)
 *   5. otherwise → unsupported_dimension_combination
 *
 * Empty dimension array defaults to sbCampaigns (most common request).
 */
export function pickSbReportType(
  dimensions: ReadonlyArray<string>
): PickSbReportTypeResult {
  const set = new Set(dimensions);
  const warnings: string[] = [];

  // SP/shared dimensions (campaignId/campaignName/campaignStatus carry forward
  // from SP — sb-fetchMetrics imports them from sp-column-map).
  // Story 35.7 (Code Review Round 2) — Portfolio + Campaign Budget cluster
  // applies to SB campaigns identically to SP campaigns (verified May 2026
  // via Amazon Ads v3 SB OpenAPI). Mirroring the SP ANY_DIMS extension
  // closes the dispatch-routing gap surfaced by Round 2.
  const ANY_DIMS = new Set([
    "campaignId",
    "campaignName",
    "campaignStatus",
    "portfolioId",
    "campaignBudgetAmount",
    "campaignBudgetCurrencyCode",
    "campaignRuleBasedBudgetAmount",
    "campaignApplicableBudgetRuleId",
    "campaignApplicableBudgetRuleName",
    "biddingStrategy",
    "campaignBudgetType",
  ]);
  const PURCHASED_PRODUCT_DIMS = new Set(["purchasedAsin", "attributionType"]);
  const AD_GROUP_DIMS = new Set(["adGroupId", "adGroupName"]);
  // SD-only dimensions that must NEVER reach SB (Round 3 Vuln 4 — typed column-
  // map keys per ad-product prevent silent mis-routing).
  const SD_ONLY_DIMS = new Set([
    "promotedAsin",
    "promotedSku",
    "targetingExpression",
    "adId",
    // Story 35.7 (Code Review Round 2) — Lead Gen dims are SD-only
    // (sdAdvertisedProduct surfaces them); SB does NOT have Lead Gen campaigns.
    "leadFormId",
    "leadFormType",
    "leadFormSchemaVersion",
  ]);
  // Story 35.7 (Code Review Round 2) — Keyword family is SP-only;
  // SB doesn't expose keyword-level reports through this dim set.
  const SP_ONLY_DIMS = new Set([
    "keywordId",
    "keyword",
    "keywordType",
    "keywordBid",
    "targetingId",
    "targetingText",
    "searchTerm",
    "placement",
    "targeting",
    "matchType",
    "advertisedAsin",
    "advertisedSku",
  ]);

  const buckets = new Set<SbReportTypeId>();
  const unknown: string[] = [];
  for (const d of set) {
    if (ANY_DIMS.has(d)) continue;
    if (SD_ONLY_DIMS.has(d)) {
      return {
        ok: false,
        error: "ad_product_dimension_mismatch",
        message: `Dimension ${d} is Sponsored Display-only and cannot be used with Sponsored Brands`,
      };
    }
    if (SP_ONLY_DIMS.has(d)) {
      return {
        ok: false,
        error: "ad_product_dimension_mismatch",
        message: `Dimension ${d} is Sponsored Products-only and cannot be used with Sponsored Brands`,
      };
    }
    if (PURCHASED_PRODUCT_DIMS.has(d)) buckets.add("sbPurchasedProduct");
    else if (AD_GROUP_DIMS.has(d)) buckets.add("sbAdGroup");
    else unknown.push(d);
  }

  if (unknown.length > 0) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Unknown dimension(s) for Sponsored Brands: ${unknown.join(", ")}`,
    };
  }

  if (buckets.size > 1) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Dimensions span multiple SB report types (${[...buckets].join(", ")})`,
    };
  }

  if (buckets.size === 1) {
    return { ok: true, reportTypeId: [...buckets][0]!, warnings };
  }

  return { ok: true, reportTypeId: "sbCampaigns", warnings };
}

/**
 * Returns the columns to request for a given (reportTypeId, requestedMetrics,
 * requestedDimensions) combo. Includes the campaign* dimensions carried over
 * from the SP map.
 */
export function buildSbColumnList(
  reportTypeId: SbReportTypeId,
  requestedMetricKeys: ReadonlyArray<string>,
  requestedDimensionKeys: ReadonlyArray<string>
): { columns: string[]; warnings: string[] } {
  const warnings: string[] = [];
  const columns = new Set<string>();

  // Dimensions on this report type — campaign* dimensions (shared with SP) are
  // always allowed; SB-specific dimensions must match the report type.
  for (const dk of requestedDimensionKeys) {
    if (
      dk === "campaignId" ||
      dk === "campaignName" ||
      dk === "campaignStatus"
    ) {
      columns.add(dk);
      continue;
    }
    const d = SB_V3_DIMENSION_MAP[dk];
    if (!d) {
      warnings.push(`Unknown SB dimension key dropped: ${dk}`);
      continue;
    }
    if (d.reportTypeId !== "any" && d.reportTypeId !== reportTypeId) {
      warnings.push(
        `Dimension ${dk} valid on ${d.reportTypeId}, dropped for ${reportTypeId}`
      );
      continue;
    }
    columns.add(d.columnName);
  }

  // Metrics on this report type.
  for (const mk of requestedMetricKeys) {
    const m = SB_V3_COLUMN_MAP[mk];
    if (!m) {
      warnings.push(`Unknown SB metric key dropped: ${mk}`);
      continue;
    }
    if (m.reportTypeId !== reportTypeId) {
      warnings.push(
        `Metric ${mk} valid on ${m.reportTypeId}, dropped for ${reportTypeId}`
      );
      continue;
    }
    columns.add(m.columnName);
  }

  return { columns: [...columns], warnings };
}

/** Returns the deduped `groupBy` values for the SB request body. */
export function buildSbGroupByList(
  reportTypeId: SbReportTypeId,
  requestedDimensionKeys: ReadonlyArray<string>
): string[] {
  const out = new Set<string>();
  for (const dk of requestedDimensionKeys) {
    if (
      dk === "campaignId" ||
      dk === "campaignName" ||
      dk === "campaignStatus"
    ) {
      out.add("campaign");
      continue;
    }
    const d = SB_V3_DIMENSION_MAP[dk];
    if (!d) continue;
    if (d.reportTypeId !== "any" && d.reportTypeId !== reportTypeId) continue;
    out.add(d.groupByValue);
  }
  if (out.size === 0) {
    if (reportTypeId === "sbCampaigns") out.add("campaign");
    else if (reportTypeId === "sbAdGroup") out.add("adGroup");
    else if (reportTypeId === "sbPurchasedProduct") out.add("purchasedAsin");
  }
  return [...out];
}

/**
 * AC #5 / Round 2 Pre-mortem E — derived `viewableRate` is `viewableImpressions
 * / measurableImpressions` (NOT `/ impressions`). Adapter does NOT compute SB
 * `viewableRate` — Amazon emits it directly. We DO compute clickThroughRate
 * because it's universally `clicks / impressions` across SP/SB/SD and Amazon
 * doesn't always emit it in v3.
 */
export function applySbDerivedMetrics(
  records: ReadonlyArray<Record<string, unknown>>
): Record<string, unknown>[] {
  return records.map((rec) => {
    const out: Record<string, unknown> = { ...rec };
    const impressions = numericOrNull(rec.impressions);
    const clicks = numericOrNull(rec.clicks);

    if (out.clickThroughRate === undefined || out.clickThroughRate === null) {
      out.clickThroughRate = safeDivide(clicks, impressions);
    }
    return out;
  });
}

function safeDivide(num: number | null, den: number | null): number | null {
  if (num === null || den === null || den === 0) return null;
  return num / den;
}
